/**
 * Tipos compartilhados pelos passos do ImportDealsWizard.
 *
 * CHANGELOG:
 *   RELEASE: 2026-05-17 v1.3.0 - `title` marcado como required no DEAL_FIELDS
 *            para paridade com o form manual de criação de negócio (Zod exige
 *            título não vazio). Backend mantém fallback "Negócio - <telefone>"
 *            como camada extra de segurança em process_deal_import_batch.
 *   RELEASE: 2026-05-16 v1.2.0 - Ampliado AUTODETECT (etiquetas/marcadores → tags
 *            e vendedor → seller) + novo tipo SellerMode usado por Step3Defaults
 *            (manual | queue | from_file). Modo "queue" reaproveita o padrão de
 *            distribuição usado em CrmIntegrationSection.
 *   RELEASE: 2026-05-16 v1.1.0 - SENSITIVE_HEADERS e remoção de Empresa/Moeda.
 *   RELEASE: 2026-05-16 v1.0.0 - Extraído do ImportDealsWizard.tsx
 */

import type { CSVRow } from '@/lib/csv-utils';

/** Workspace do CRM (filtra pipelines listadas no Step 3). */
export interface Workspace {
  id: string;
  name: string;
}

/** Pipeline simples do CRM para os selects do step 3. */
export interface Pipeline {
  id: string;
  name: string;
  workspace_id?: string | null;
}

/** Fase de pipeline simples para os selects do step 3. */
export interface Phase {
  id: string;
  name: string;
  pipeline_id: string;
}

/** Campo de destino que pode ser mapeado a partir de uma coluna do CSV. */
export interface DealFieldDef {
  key: string;
  label: string;
  required?: boolean;
}

/**
 * Lista de campos suportados no mapeamento (csv_column -> deal_field).
 *
 * RELEASE: 2026-05-16 v1.1.0 - Removidos `company_name` (Empresa) e `currency`
 *          (Moeda). Empresa não é importável (risco cross-tenant); moeda é
 *          sempre BRL e definida internamente pelo sistema.
 */
export const DEAL_FIELDS: DealFieldDef[] = [
  { key: 'title', label: 'Título do Negócio (obrigatório)', required: true },
  { key: 'contact_phone', label: 'Telefone (obrigatório)', required: true },
  { key: 'contact_name', label: 'Nome do Contato' },
  { key: 'contact_email', label: 'E-mail do Contato' },
  { key: 'value', label: 'Valor' },
  { key: 'source', label: 'Origem' },
  { key: 'description', label: 'Descrição' },
  { key: 'pipeline', label: 'Pipeline (por nome)' },
  { key: 'phase', label: 'Fase (por nome)' },
  { key: 'seller', label: 'Vendedor (e-mail)' },
  { key: 'tags', label: 'Tags (separadas por ;)' },
];

/** Heurística de autodetect de cabeçalhos do CSV (lower-case). */
export const AUTODETECT: Record<string, string> = {
  titulo: 'title', title: 'title', negocio: 'title',
  telefone: 'contact_phone', phone: 'contact_phone', celular: 'contact_phone',
  nome: 'contact_name', name: 'contact_name',
  email: 'contact_email', 'e-mail': 'contact_email',
  valor: 'value', value: 'value',
  origem: 'source', source: 'source',
  descricao: 'description', descrição: 'description', description: 'description',
  pipeline: 'pipeline',
  fase: 'phase', etapa: 'phase', phase: 'phase',
  vendedor: 'seller', seller: 'seller', responsavel: 'seller', responsável: 'seller',
  // v1.2.0: tags em variantes pt-br
  tags: 'tags', tag: 'tags', etiquetas: 'tags', etiqueta: 'tags',
  marcadores: 'tags', marcador: 'tags',
};

/**
 * Cabeçalhos sensíveis que NUNCA devem ser importados do CSV.
 * Bloqueados server-side em `process_deal_import_batch` v1.0.2 e
 * `validate_deal_import_job` v1.0.4 (status/won/lost).
 *
 * v1.0.4: Adicionados status/is_won/ganho/perdido — importar deal já como
 * "ganho" burlaria triggers do CRM (cobrança, contrato, comissão etc.).
 */
export const SENSITIVE_HEADERS = [
  'moeda', 'currency',
  'empresa', 'company', 'company_name',
  // v1.0.4 - bloqueio de status/ganho/perdido vindos do CSV
  'status', 'situacao', 'situação',
  'is_won', 'is_lost',
  'ganho', 'perdido', 'won', 'lost',
];

/**
 * Modo de atribuição de vendedor (v1.2.0).
 * Reproduz o padrão do componente CrmIntegrationSection
 * (none|manual|distribution_queue), trocando "none" por "from_file".
 */
export type SellerMode = 'manual' | 'queue' | 'from_file';

/** Fila de distribuição usada quando sellerMode = 'queue'. */
export interface DistributionQueueOption {
  id: string;
  name: string;
  distribution_method: string;
}

/** Re-export para conveniência. */
export type { CSVRow };

/** Contadores devolvidos pelo dry-run de validação. */
export interface ValidationCounters {
  total_rows: number;
  valid_count: number;
  invalid_count: number;
  duplicate_count: number;
  blacklisted_count: number;
}
